<script setup lang="ts">
import type { Schemas, RequestParameters } from "#shopware";
import { getProductRoute } from "@shopware/helpers";
import productInformation from "../../components/shared/SharedProductInformationExpand.vue";

const { t } = useI18n();
const { pushError } = useNotifications();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const { apiClient } = useShopwareContext();
const { groupProducts, getDisplayVariant } = useProductHelper();

const loading = ref(false);
const isParkside = ref(false);

const productInformationExpand = ref<InstanceType<
  typeof productInformation
> | null>(null);

const productModel = ref("");
const productNumber = ref("");
const ean = ref("");
const ian = ref("");
const enum SearchField {
  NONE,
  PRODUCT_MODEL,
  PRODUCT_NUMBER,
  EAN,
  IAN,
  ERROR,
}
const searchField = computed(() => {
  if (!productModel.value && !productNumber.value && !ean.value && !ian.value)
    return SearchField.NONE;
  if (!!productModel.value) {
    if (!productNumber.value && !ean.value && !ian.value) {
      return SearchField.PRODUCT_MODEL;
    } else {
      return SearchField.ERROR;
    }
  }
  if (!!productNumber.value) {
    if (!productModel.value && !ean.value && !ian.value) {
      return SearchField.PRODUCT_NUMBER;
    } else {
      return SearchField.ERROR;
    }
  }
  if (!!ean.value) {
    if (!productModel.value && !productNumber.value && !ian.value) {
      return SearchField.EAN;
    } else {
      return SearchField.ERROR;
    }
  }
  if (!!ian.value) {
    if (!productModel.value && !productNumber.value && !ean.value) {
      return SearchField.IAN;
    } else {
      return SearchField.ERROR;
    }
  }
  return SearchField.ERROR;
});
const replacements: Record<string, string> = {
  "²": "2",
  "³": "3",
  // ...
};
const searchFilter = computed<RequestParameters<"searchPage">["filter"]>(() => {
  switch (searchField.value) {
    case SearchField.PRODUCT_MODEL:
      return [
        {
          field: "customFields.productModel",
          type: "equals",
          value: productModel.value
            .toLowerCase()
            .replace(/[²³]/g, (c) => replacements[c])
            .replace(/[^a-z0-9]/g, ""),
        },
      ];
    case SearchField.PRODUCT_NUMBER:
      return [
        { field: "productNumber", type: "equals", value: productNumber.value },
      ];
    case SearchField.EAN:
      return [{ field: "ean", type: "equals", value: ean.value }];
    case SearchField.IAN:
      return [{ field: "customFields.ian", type: "equals", value: ian.value }];
    default:
      return [];
  }
});

const result = ref();
const products = ref<Schemas["Product"][]>([]);
async function productSearch() {
  loading.value = true;
  result.value = await apiClient
    .invoke("readProduct post /product", {
      body: {
        filter: [
          {
            field: "customFields.inPartsSearch",
            type: "equals",
            value: "true",
          },
          ...searchFilter.value!,
        ],
        associations: {
          children: {
            associations: {
              cover: {},
              deliveryTime: {},
              seoUrls: {},
            },
          },
        },
      },
    })
    .then((x) => x.data);

  if (result.value.total === 0) {
    pushError(t("product.noProductsFound"));
    loading.value = false;
    return;
  } else {
    loading.value = false;

    productModel.value = "";
    productNumber.value = "";
    ean.value = "";
    ian.value = "";
  }

  if (result.value.total === 1) {
    loading.value = true;
    navigateTo(
      getProductRoute(result.value.elements[0]).path +
        t("spareParts.sparePartsRoute"), // Routes to #spare-parts Tab directly
    );
    return;
  } else {
    const grouped = groupProducts(result.value.elements);
    if (grouped.length === 1) {
      navigateTo(getProductRoute(getDisplayVariant(grouped[0])));
      return;
    }
    products.value = grouped.map(getDisplayVariant);
  }

  loading.value = false;
}

const setHoverTypeExpose = (type: number) => {
  productInformationExpand.value?.setHoverType(type);
};

useBreadcrumbs([
  {
    name: t("breadcrumbs.machineArchive"),
    path: "/spare-parts",
  },
]);

const foundMachineDescs = ref([]);

const handleInputDebounced = useDebounceFn(handleInput, 200);

const isMaschineDescsLoading = ref(false);
async function handleInput(event) {
  const input = event.target.value;
  if (!input || input.length <= 1) return;

  isMaschineDescsLoading.value = true;

  if (input.length > 0) {
    isDropdownOpen.value = true;
    isMaschineDescsLoading.value = true;
  }

  const response = await $fetch(
    `/api/model/get_model_description?desc=${input}`,
    {
      method: "GET",
    },
  );
  foundMachineDescs.value = response;

  foundMachineDescs.value.length > 0
    ? (isDropdownOpen.value = true)
    : (isDropdownOpen.value = false);
  isMaschineDescsLoading.value = false;
}

const dropDown = ref(null);
onClickOutside(dropDown, (event) => (isDropdownOpen.value = false));

const isDropdownOpen = ref(false);
function setProductModel(item: any) {
  productModel.value = item;
  isDropdownOpen.value = false;
}

function emptyOther(self: any) {
  if (self != "productNumber") productNumber.value = "";

  if (self != "ian") ian.value = "";

  if (self != "productModel") productModel.value = "";

  if (self != "ean") ean.value = "";
}
</script>

<template>
  <LayoutBreadcrumbs />
  <main class="custom-container xl:mb-15">
    <h1 class="font-700 text-[30px] c-scheppach-primary-700">
      {{ t("spareParts.machineArchive") }}
    </h1>

    <p class="md:w-3xl my-3">
      {{ t("spareParts.headerContent") }}
    </p>

    <section class="bg-white pt-5 mb-10">
      <div class="grid grid-cols-1 gap-2">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ScheppachTextInput
            v-model="productNumber"
            :label="t('product.articleNumber')"
            @mouseover="setHoverTypeExpose(2)"
            @mouseout="setHoverTypeExpose(0)"
            @keydown.enter="productSearch"
            @keydown="emptyOther('productNumber')"
          />

          <ScheppachTextInput
            v-model="ian"
            label="IAN/PLU"
            @mouseover="
              isParkside ? setHoverTypeExpose(3) : setHoverTypeExpose(0)
            "
            @mouseout="setHoverTypeExpose(0)"
            @keydown.enter="productSearch"
            @keydown="emptyOther('ian')"
          />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div class="relative">
            <ScheppachTextInput
              v-model="productModel"
              :label="t('product.modelName')"
              @mouseover="setHoverTypeExpose(1)"
              @mouseout="setHoverTypeExpose(0)"
              @keydown.enter="productSearch"
              @input="handleInputDebounced"
              @keydown="emptyOther('productModel')"
            />

            <div
              v-if="isDropdownOpen"
              ref="dropDown"
              class="b-t-none b-1 b-scheppach-primary-500 c-scheppach-primary-700 bg-scheppach-primary-50 h-200px w-full rd-b absolute z-10 overflow-x-hidden overflow-auto"
            >
              <ScheppachSpinner v-if="isMaschineDescsLoading" />
              <div class="p-2 bg-scheppach-primary-500 font-bold c-white">
                {{ $t("search.resultsHeader") }}: {{ foundMachineDescs.length }}
              </div>
              <div
                v-for="(item, index) in foundMachineDescs"
                :key="index"
                class="hover:bg-scheppach-primary-100 hover:font-bold cursor-pointer"
                @click="setProductModel(item)"
              >
                <div class="p-2 lg:hover:translate-x-[5px] duration-100 w-full">
                  {{ item }}
                </div>
              </div>
            </div>
          </div>

          <ScheppachTextInput
            v-model="ean"
            label="EAN"
            @mouseover="setHoverTypeExpose(4)"
            @mouseout="setHoverTypeExpose(0)"
            @keydown.enter="productSearch"
            @keydown="emptyOther('ean')"
          />
        </div>
      </div>
      <button
        type="button"
        class="w-full sm:w-auto mt-5 uppercase py-2 px-5 rd c-white bg-scheppach-primary-500 hover:bg-scheppach-primary-700"
        :disabled="
          searchField == SearchField.NONE || searchField == SearchField.ERROR
        "
        @click="productSearch"
      >
        {{ t("breadcrumbs.search") }}
      </button>
    </section>

    <!-- Search Results Slider -->
    <div v-if="result?.total > 0">
      <div
        class="inline-block px-3 py-2 c-scheppach-primary-500 b b-scheppach-primary-500 bg-scheppach-primary-50 rd"
      >
        <div class="flex items-center gap-4 sm:gap-2">
          <i class="i-sl-info inline-block text-9 sm:text-5"></i>
          <p>{{ t("spareParts.detailedResults") }}</p>
        </div>
      </div>
      <ProductSlider
        class="mb-20"
        :title="
          result?.total > 1
            ? t('search.resultsHeader')
            : t('search.resultHeader')
        "
        :products="products"
      />
    </div>

    <section class="mb-10">
      <SharedProductInformationExpand ref="productInformationExpand" />
    </section>

    <section>
      <div class="c-scheppach-primary-700 text-[30px] mt-10 mb-2">
        {{ t("spareParts.anyQuestions") }}
      </div>

      <!-- <div class="bg-scheppach-primary-50 h-350px overflow-hidden mb-12">
        <div class="xl:flex xl:justify-between">
          <div class="p-6">
            <div
              class="font-700 c-scheppach-primary-700 text-[30px] capitalize"
            >
              {{ t("spareParts.customerService") }}
            </div>
            <p class="c-scheppach-primary-700 text-18px w-sm mt-3 mb-10">
              {{ t("spareParts.customerServiceText") }}
            </p>
            <NuxtLink
              class="c-scheppach-primary-500 hover:c-scheppach-primary-700 underline underline-offset-5 font-700"
              :to="formatLink('https://www.scheppach.com/de/service')"
              target="_blank"
              >{{ t("spareParts.toCustomerService") }}</NuxtLink
            >
          </div>
          <img
            class="hidden xl:block"
            src="../../../public/images/sparePartsImage1.png"
            alt=""
          />
        </div>
      </div> -->

      <div class="bg-scheppach-primary-50 h-350px overflow-hidden">
        <div class="xl:flex xl:justify-between">
          <img
            class="hidden xl:block"
            src="../../../public/images/sparePartsImage2.png"
            alt=""
          />
          <div class="p-6">
            <div
              class="font-700 c-scheppach-primary-700 text-[30px] capitalize"
            >
              {{ t("spareParts.contactFormular") }}
            </div>
            <p class="c-scheppach-primary-700 text-18px w-sm mt-3 mb-10">
              {{ t("spareParts.contactText") }}
            </p>
            <NuxtLink
              class="c-scheppach-primary-500 hover:c-scheppach-primary-700 underline underline-offset-5 font-700"
              :to="'/contact'"
              >{{ t("spareParts.toContactFormular") }}</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <ScheppachSpinner v-if="loading" />
  </main>
</template>
